import { useCallback } from 'react';
import { DRAG_THRESHOLD } from '../utils/canvasUtils';
import {
  startMissionToSidebarDrag,
  updateMissionToSidebarPointer,
  restoreFlowchartCanvasPointerEvents,
} from '../../../../../utils/missionToSidebarBridge';

/**
 * mousedown + threshold check — starts canvas drag and optional sidebar bridge.
 */
export function useFlowchartDragStart({
  tasks,
  nodePositions,
  customPositions,
  selectedNodes,
  autoArrange,
  disableDragRef,
  campaignId,
  potentialDragNode,
  setPotentialDragNode,
  setHasDragged,
  setAutoArrangeBlocked,
  setDraggingNode,
  draggingNodeRef,
  dragStartPosRef,
  dragStartCanvasPosRef,
  dragOffsetRef,
  initialPositionsRef,
  currentDragPositionsRef,
}) {
  const handleNodeDragStart = useCallback((taskId, e, canvasPos) => {
    restoreFlowchartCanvasPointerEvents();
    e.stopPropagation();

    if (e.button !== 0) return;

    setPotentialDragNode(taskId);
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };

    if (canvasPos) {
      dragStartCanvasPosRef.current = canvasPos;
    }

    setHasDragged(false);
  }, [
    setPotentialDragNode,
    dragStartPosRef,
    dragStartCanvasPosRef,
    setHasDragged,
  ]);

  const checkDragThreshold = useCallback((e, canvasPos) => {
    if (!potentialDragNode || draggingNodeRef.current) return false;

    const dx = e.clientX - dragStartPosRef.current.x;
    const dy = e.clientY - dragStartPosRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > DRAG_THRESHOLD) {
      if (disableDragRef?.current) {
        return false;
      }

      if (autoArrange) {
        setAutoArrangeBlocked(true);
        setTimeout(() => setAutoArrangeBlocked(false), 2000);
        setPotentialDragNode(null);
        return false;
      }

      const nodePos = customPositions[potentialDragNode] || nodePositions[potentialDragNode] || { x: 0, y: 0 };
      setDraggingNode(potentialDragNode);
      draggingNodeRef.current = potentialDragNode;

      const draggedTask = tasks.find((t) => t.id === potentialDragNode);
      const isSingleNodeDrag = !(selectedNodes.has(potentialDragNode) && selectedNodes.size > 1);

      if (
        isSingleNodeDrag
        && draggedTask
        && draggedTask.nodeType !== 'milestone'
        && draggedTask.nodeType !== 'note'
      ) {
        const sourceSprintId = draggedTask.sourceCampaign?.id || campaignId;
        if (sourceSprintId) {
          startMissionToSidebarDrag({
            taskId: draggedTask.id,
            sourceSprintId,
            taskName: draggedTask.name,
            taskIcon: draggedTask.icon,
            source: 'flow',
            sourceTask: draggedTask,
            sourceNodePositions: { ...nodePositions, ...customPositions },
            nestedSprintId: draggedTask.nestedSprintId || null,
          });
          updateMissionToSidebarPointer(e.clientX, e.clientY);
        } else {
          console.warn('[M2S] bridge NOT started — no sourceSprintId for drag', {
            taskId: potentialDragNode,
          });
        }
      } else {
        console.log('[M2S] bridge NOT started for this drag', {
          taskId: potentialDragNode,
          taskFound: !!draggedTask,
          isSingleNodeDrag,
          selectedCount: selectedNodes.size,
          nodeType: draggedTask?.nodeType,
        });
      }

      const canvas = document.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.style.pointerEvents = 'none';
      }

      const initialCanvasPos = dragStartCanvasPosRef.current;
      dragOffsetRef.current = {
        x: initialCanvasPos.x - nodePos.x,
        y: initialCanvasPos.y - nodePos.y,
      };
      setHasDragged(true);

      const initPos = {};
      if (selectedNodes.has(potentialDragNode) && selectedNodes.size > 1) {
        selectedNodes.forEach((nodeId) => {
          initPos[nodeId] = customPositions[nodeId] || nodePositions[nodeId] || { x: 0, y: 0 };
        });
      } else {
        initPos[potentialDragNode] = nodePos;
        if (draggedTask?.nodeType !== 'note') {
          tasks.forEach((t) => {
            if (t.nodeType === 'note' && t.noteAnchor?.hostTaskId === potentialDragNode) {
              initPos[t.id] = customPositions[t.id] || nodePositions[t.id] || { x: 0, y: 0 };
            }
          });
        }
      }
      initialPositionsRef.current = initPos;
      currentDragPositionsRef.current = {};

      return true;
    }
    return false;
  }, [
    potentialDragNode,
    customPositions,
    nodePositions,
    selectedNodes,
    autoArrange,
    disableDragRef,
    tasks,
    campaignId,
    draggingNodeRef,
    dragStartPosRef,
    dragStartCanvasPosRef,
    dragOffsetRef,
    initialPositionsRef,
    currentDragPositionsRef,
    setDraggingNode,
    setPotentialDragNode,
    setHasDragged,
    setAutoArrangeBlocked,
  ]);

  return { handleNodeDragStart, checkDragThreshold };
}
